import { useEffect, useRef } from 'react';

export default function Waves({
  lineColor = '#5227ff',
  backgroundColor = 'transparent',
  waveSpeedX = 0.02,
  waveSpeedY = 0.01,
  waveAmpX = 40,
  waveAmpY = 20,
  friction = 0.9,
  tension = 0.01,
  maxCursorMove = 120,
  xGap = 12,
  yGap = 36,
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animId;
    const mouse = { x: -9999, y: -9999 };
    let points = [];
    let t = 0;
    let numCols = 0;
    let numRows = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
      numCols = Math.ceil(canvas.width / xGap) + 2;
      numRows = Math.ceil(canvas.height / yGap) + 2;
      initPoints();
    };

    const initPoints = () => {
      points = [];
      for (let row = 0; row < numRows; row++) {
        for (let col = 0; col < numCols; col++) {
          points.push({
            ox: col * xGap,
            oy: row * yGap,
            x: col * xGap,
            y: row * yGap,
            vx: 0,
            vy: 0,
            row,
            col,
          });
        }
      }
    };

    const animate = () => {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (backgroundColor !== 'transparent') {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      ctx.strokeStyle = lineColor;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.65;

      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        const waveX = Math.sin(t * waveSpeedX + p.row * 0.5) * waveAmpX;
        const waveY = Math.sin(t * waveSpeedY + p.col * 0.3 + p.row * 0.2) * waveAmpY;

        const tx = p.ox + waveX;
        const ty = p.oy + waveY;

        const dx = tx - mouse.x;
        const dy = ty - mouse.y;
        const dist = Math.sqrt(dx * dx + dy * dy);

        if (dist < maxCursorMove) {
          const force = 1 - dist / maxCursorMove;
          p.vx += dx * force * tension;
          p.vy += dy * force * tension;
        }

        p.vx += (tx - p.x) * tension * 2;
        p.vy += (ty - p.y) * tension * 2;
        p.vx *= friction;
        p.vy *= friction;
        p.x += p.vx;
        p.y += p.vy;
      }

      for (let row = 0; row < numRows; row++) {
        ctx.beginPath();
        for (let col = 0; col < numCols; col++) {
          const idx = row * numCols + col;
          if (idx >= points.length) break;
          const p = points[idx];
          if (col === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        }
        ctx.stroke();
      }

      animId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
    };

    resize();
    animate();

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [lineColor, backgroundColor, waveSpeedX, waveSpeedY, waveAmpX, waveAmpY, friction, tension, maxCursorMove, xGap, yGap]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'block',
      }}
    />
  );
}
